/**
 * "This month at {tenant}" — the four stat tiles + the 14-day activity
 * histogram, straight from the Home overview's tz-aware aggregates. When the
 * server had to truncate its aggregation window the values are lower bounds
 * and render with a "≥" prefix rather than pretending to be exact.
 */
import type { HomeStats as HomeStatsData } from '@portal/contracts'
import { Link } from 'react-router-dom'
import type { Messages } from '../../i18n'
import { useI18n } from '../../i18n'
import { fmtDayShort, fmtDurationClock, fmtMonthLong, isWeekend } from './home-format'

const CHART_MAX_PX = 56

export function HomeStats({ stats, tenantName }: { stats: HomeStatsData; tenantName: string }) {
  const { t, locale, formatNumber } = useI18n()
  const m = t.home
  const now = new Date()
  const monthName = fmtMonthLong(now, locale)
  const prevMonthName = fmtMonthLong(new Date(now.getFullYear(), now.getMonth() - 1, 1), locale)
  const approx = stats.truncated ? '≥ ' : ''

  const first = stats.runsPerDay[0]
  const last = stats.runsPerDay[stats.runsPerDay.length - 1]
  const chartMax = Math.max(1, ...stats.runsPerDay.map((d) => d.count))

  return (
    <section className="home-month card">
      <div className="home-month-head">
        <span className="home-month-title">{m.thisMonthAt(tenantName)}</span>
        <span className="home-month-range muted">{m.monthRange(monthName, now.getDate())}</span>
        {stats.truncated && (
          <span className="chip" title={m.partialTooltip}>
            {m.partialChip}
          </span>
        )}
        <Link className="home-more" to="/conversations">
          {m.fullHistory} →
        </Link>
      </div>
      <div className="home-month-grid">
        <StatTile
          label={m.statRunsCompleted}
          value={`${approx}${formatNumber(stats.runsCompleted)}`}
          sub={paceSub(stats, prevMonthName, m)}
        />
        <StatTile
          label={m.statSuccessRate}
          value={
            stats.successRate == null
              ? '—'
              : formatNumber(stats.successRate, {
                  style: 'percent',
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1
                })
          }
          sub={
            stats.runsCompleted > 0
              ? { text: m.runsOfTotal(stats.succeeded, stats.runsCompleted), tone: 'muted' }
              : { text: m.noFinishedRuns, tone: 'muted' }
          }
        />
        <StatTile
          label={m.statDocsDelivered}
          value={`${approx}${formatNumber(stats.docsDelivered)}`}
          sub={
            stats.docsDeliveredLast7Days > 0
              ? { text: m.docsThisWeek(stats.docsDeliveredLast7Days), tone: 'up' }
              : { text: m.noneThisWeek, tone: 'muted' }
          }
        />
        <StatTile
          label={m.statMedianTurnaround}
          value={fmtDurationClock(stats.medianDurationMs)}
          sub={{ text: m.fromLaunchToFiles, tone: 'muted' }}
        />
        <div className="home-chart">
          <span className="stat-label">{m.runsPerDay14}</span>
          <div className="home-chart-bars">
            {stats.runsPerDay.map((d) => (
              <div
                key={d.date}
                className={`home-chart-bar${isWeekend(d.date) ? ' is-weekend' : ''}`}
                style={{ height: Math.max(3, Math.round((d.count / chartMax) * CHART_MAX_PX)) }}
                title={m.dayRuns(fmtDayShort(d.date, locale), d.count)}
              />
            ))}
          </div>
          <div className="home-chart-axis mono">
            <span>{first ? fmtDayShort(first.date, locale) : ''}</span>
            <span>{last ? fmtDayShort(last.date, locale) : ''}</span>
          </div>
        </div>
      </div>
    </section>
  )
}

interface StatSub {
  text: string
  tone: 'up' | 'down' | 'muted'
}

function paceSub(stats: HomeStatsData, prevMonthName: string, m: Messages['home']): StatSub | null {
  const prev = stats.runsCompletedPrevMonthToDate
  if (prev == null) return null
  if (prev === 0) return stats.runsCompleted > 0 ? { text: m.firstActiveMonth, tone: 'muted' } : null
  const pct = Math.round(((stats.runsCompleted - prev) / prev) * 100)
  if (pct > 0) return { text: m.paceUp(pct, prevMonthName), tone: 'up' }
  if (pct < 0) return { text: m.paceDown(Math.abs(pct), prevMonthName), tone: 'down' }
  return { text: m.paceEven(prevMonthName), tone: 'muted' }
}

function StatTile({ label, value, sub }: { label: string; value: string; sub: StatSub | null }) {
  return (
    <div className="home-stat">
      <span className="stat-label">{label}</span>
      <span className="stat-value mono">{value}</span>
      {sub && <span className={`stat-sub is-${sub.tone}`}>{sub.text}</span>}
    </div>
  )
}
